/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  AIMessageDraft,
  MessageScheduleUpdatedDetails,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import AutoSendToNewMatches from 'bundles/enterprise-admin-messaging-v2/components/common/AutoSendToNewMatches';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import { useRefreshSentMessages } from 'bundles/enterprise-admin-messaging-v2/hooks/useSentMessages';
import { useUpdateCampaignSchedule } from 'bundles/enterprise-admin-messaging-v2/hooks/useUpdateCampaignSchedule';
import {
  MESSAGING_BUTTON,
  MESSAGING_CHECKBOX,
  MESSAGING_MODAL,
  MESSAGING_RADIO,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
  onDone: () => void;
  /**
   * Whether to show the "auto-send to new matches" automation option. Defaults
   * to true; entry points that shouldn't offer automation (e.g. the
   * program-card reminder) pass false to hide it.
   */
  showAutomate?: boolean;
  /**
   * The scopedTriggerId of the message that was just sent. Present only for
   * backend-audience sends (absent for the generic AI-compose mock send), and
   * used to start a new campaign schedule on Done when auto-send is enabled.
   */
  triggerId?: string;
  /** Fired on Done with the final auto-send choice, after any schedule write. */
  onScheduleUpdated?: (details: MessageScheduleUpdatedDetails) => void | Promise<void>;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  // Persistent live region wrapper. Kept outside `body` so the always-mounted
  // empty element doesn't introduce a stray flex gap; the margin only applies
  // once the banner is actually rendered inside it.
  errorRegion: css`
    &:not(:empty) {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  supportText: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

/**
 * Final confirmation screen. Confirms the send count and lets the admin
 * opt in to auto-sending the same message to new learners that match this
 * audience in the future, on a selectable cadence.
 */
const MessageSentScreen = ({
  draft,
  updateDraft,
  onDone,
  showAutomate = true,
  triggerId,
  onScheduleUpdated,
}: Props) => {
  const track = useTracker();
  const trackingData = useMessagingTrackingData();
  const { updateCampaignSchedule, isUpdating } = useUpdateCampaignSchedule();
  const refreshSentMessages = useRefreshSentMessages();
  const [showScheduleError, setShowScheduleError] = React.useState(false);

  const effectiveCount = draft.audienceDelta
    ? draft.audienceCount - draft.audienceDelta.removedIds.length + draft.audienceDelta.addedIds.length
    : draft.audienceCount;

  // Modal impression for the post-send confirmation screen.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.SEND_SUCCESS } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Track the auto-send toggle flip, then persist it on the draft. */
  const handleEnabledChange = React.useCallback<(autoSendEnabled: boolean) => void>(
    (autoSendEnabled) => {
      track('click_checkbox', {
        checkbox: { name: MESSAGING_CHECKBOX.AUTO_SEND_TOGGLE, checked: autoSendEnabled },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
      });
      updateDraft({ autoSendEnabled });
    },
    [track, trackingData, updateDraft]
  );

  /** Track the cadence selection, then persist it on the draft. */
  const handleCadenceChange = React.useCallback<(autoSendCadence: AIMessageDraft['autoSendCadence']) => void>(
    (autoSendCadence) => {
      track('change_radio', {
        radio: { id: MESSAGING_RADIO.AUTO_SEND_FREQUENCY, value: autoSendCadence },
        ...trackingData,
        modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
      });
      updateDraft({ autoSendCadence });
    },
    [track, trackingData, updateDraft]
  );

  /**
   * Track the Done completion, then start the new campaign schedule. Done is
   * blocked on this completing: a failure keeps the admin on this screen with
   * a retry-able error instead of silently dropping the automation.
   */
  const handleDone = React.useCallback<() => Promise<void>>(async () => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.DONE },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.SEND_SUCCESS },
    });
    let scheduleTiming: AIMessageDraft['autoSendCadence'] | undefined;
    if (showAutomate && triggerId && draft.autoSendEnabled) {
      const result = await updateCampaignSchedule({ triggerId, cadence: draft.autoSendCadence });
      if (result.status === 'error') {
        setShowScheduleError(true);
        return;
      }
      // The schedule just started/changed the message's cadence — refresh the
      // table so it reflects the new frequency without a manual page reload.
      refreshSentMessages();
      scheduleTiming = draft.autoSendCadence;
    }
    if (showAutomate) {
      onScheduleUpdated?.({
        messagingCohortCount: effectiveCount,
        autoSendEnabled: draft.autoSendEnabled,
        ...(draft.autoSendEnabled ? { autoSendTiming: draft.autoSendCadence } : {}),
        ...(scheduleTiming ? { scheduleTiming } : {}),
      });
    }
    setShowScheduleError(false);
    onDone();
  }, [
    track,
    trackingData,
    onDone,
    onScheduleUpdated,
    showAutomate,
    triggerId,
    draft.autoSendEnabled,
    draft.autoSendCadence,
    effectiveCount,
    updateCampaignSchedule,
    refreshSentMessages,
  ]);

  return (
    <React.Fragment>
      <Dialog.Content>
        {/* Persistent live region: the wrapper stays mounted so screen readers
            announce the error when it appears — only the content inside is
            conditional, never the live region itself. */}
        <div role="alert" css={styles.errorRegion}>
          {showScheduleError && (
            <InlineNotification
              severity="error"
              title={_t('Unable to schedule the message')}
              onDismiss={() => setShowScheduleError(false)}
              data-testid="message-sent-schedule-error"
            >
              {_t('Please try again.')}
            </InlineNotification>
          )}
        </div>
        <div css={styles.body}>
          <Typography2 component="p" variant="subtitleLarge">
            {effectiveCount > 0 ? (
              <FormattedMessage
                message={_t('Your message will be sent to {count, plural, one {# learner} other {# learners}}.')}
                count={effectiveCount}
              />
            ) : (
              _t('Your message will be sent to learners.')
            )}
          </Typography2>
          <Typography2 component="p" variant="bodyPrimary" css={styles.supportText}>
            {_t("This may take a few moments. You can close this, we'll handle the rest.")}
          </Typography2>

          {showAutomate && (
            <AutoSendToNewMatches
              enabled={draft.autoSendEnabled}
              cadence={draft.autoSendCadence}
              onEnabledChange={handleEnabledChange}
              onCadenceChange={handleCadenceChange}
            />
          )}
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          variant="primary"
          onClick={handleDone}
          disabled={isUpdating}
          loading={isUpdating}
          data-testid="ai-message-modal-done"
        >
          {_t('Done')}
        </Button>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default MessageSentScreen;
